<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { EventKind, EventFrequency, PensionType, PENSION_TYPE_LABELS, PENSION_VALIDATION, getPensionThresholdLabel } from '@fire-calc/domain-ts';
import StatusPill from './StatusPill.vue';
import ErrorSlot from './ErrorSlot.vue';
import RequiredLabel from './RequiredLabel.vue';
import InputFieldGroup from './InputFieldGroup.vue';
import PercentageInput from './PercentageInput.vue';

function getPensionDefaultGrowth(type: PensionType): number {
  return PENSION_VALIDATION[type]?.growthRate ?? 0;
}

export interface EventFormData {
  kind: EventKind;
  label: string;
  amount: number;
  frequency: 'monthly' | 'yearly';
  period: 'ongoing' | 'years' | 'once';
  durationYears: number;
  startAge: number;
  inflationAdjusted: boolean;
  pensionType?: PensionType | undefined;
  growthRate?: number | undefined;
}

interface ScenarioEventLike {
  id: string;
  kind: EventKind;
  label: string;
  amount: number;
  startAge: number;
  frequency?: 'monthly' | 'yearly';
  durationYears?: number;
  inflationAdjusted?: boolean;
  enabled?: boolean;
  pensionType?: PensionType;
  growthRate?: number;
}

const props = withDefaults(
  defineProps<{
    event?: ScenarioEventLike;
    currentAge: number;
    getText: (key: string, params?: Record<string, unknown>) => string;
    existingEvents?: ScenarioEventLike[];
    initialKind?: EventKind;
  }>(),
  { existingEvents: () => [] as ScenarioEventLike[] },
);

const emit = defineEmits<{
  create: [data: EventFormData];
  update: [data: EventFormData];
  saveAsNew: [data: EventFormData];
  close: [];
}>();

function getPeriod(durationYears?: number): 'ongoing' | 'years' | 'once' {
  if (durationYears == null) return 'ongoing';
  if (durationYears === 0) return 'ongoing';
  if (durationYears === 1) return 'once';
  return 'years';
}

const isEdit = computed(() => props.event != null);
const kind = ref<EventKind>(props.event?.kind ?? props.initialKind ?? EventKind.extraExpense);

const label = ref('');
const amount = ref(0);
const frequency = ref<'monthly' | 'yearly'>(EventFrequency.monthly);
const period = ref<'ongoing' | 'years' | 'once'>('ongoing');
const durationYears = ref(0);
const startAge = ref(props.currentAge > 0 ? props.currentAge : 0);
const inflationAdjusted = ref(true);

const showPension = ref(false);
const pensionType = ref<PensionType | undefined>(undefined);
const growthRate = ref<number | undefined>(undefined);
const showPensionDropdown = ref(false);

function togglePensionDropdown(): void {
  showPensionDropdown.value = !showPensionDropdown.value;
}

function selectPensionType(type: PensionType): void {
  pensionType.value = type;
  showPensionDropdown.value = false;
}

function onPensionDropdownClickOutside(e: MouseEvent): void {
  if (!showPensionDropdown.value) return;
  const target = e.target as Node;
  const dialog = document.querySelector('[data-testid="event-dialog"]') as Node | null;
  if (dialog && !dialog.contains(target)) {
    showPensionDropdown.value = false;
  }
}

const pensionVisible = computed(() => kind.value === EventKind.income);

const startAgeDisplayValue = ref('');
const amountInputValue = ref('');

function formatCzkAmount(value: number): string {
  if (value <= 0) return '';
  const rounded = Math.round(value);
  const str = String(rounded);
  let buf = '';
  for (let i = 0; i < str.length; i++) {
    if (i > 0 && (str.length - i) % 3 === 0) buf += ' ';
    buf += str[i];
  }
  return buf;
}

let lastAmountSynced = '';

function updateAmountInput(): void {
  const formatted = formatCzkAmount(amount.value);
  if (formatted !== lastAmountSynced && formatted !== amountInputValue.value) {
    amountInputValue.value = formatted;
    lastAmountSynced = formatted;
  } else if (formatted === amountInputValue.value) {
    lastAmountSynced = formatted;
  }
}

function onAmountInput(e: Event): void {
  const target = e.target as HTMLInputElement;
  const raw = target.value.replace(/[^0-9]/g, '');
  amount.value = raw === '' ? 0 : Number(raw);
  target.value = formatCzkAmount(amount.value);
  lastAmountSynced = target.value;
  amountInputValue.value = target.value;
}

function onAmountFocus(): void {
  const rawVal = amount.value > 0 ? String(amount.value) : '';
  amountInputValue.value = rawVal;
  lastAmountSynced = rawVal;
}

function onAmountBlur(): void {
  const formatted = formatCzkAmount(amount.value);
  amountInputValue.value = formatted;
  lastAmountSynced = formatted;
}

function updateStartAgeDisplay(): void {
  startAgeDisplayValue.value = startAge.value > 0 ? String(startAge.value) : '';
}

watch(
  () => props.event,
  (val) => {
    if (val) {
      kind.value = val.kind;
      label.value = val.label;
      amount.value = val.amount;
      frequency.value = val.frequency ?? EventFrequency.monthly;
      period.value = getPeriod(val.durationYears);
      durationYears.value = val.durationYears ?? 0;
      startAge.value = val.startAge;
      inflationAdjusted.value = val.inflationAdjusted ?? true;
      showPension.value = val.pensionType != null;
      pensionType.value = val.pensionType;
      growthRate.value = val.growthRate ?? (val.pensionType ? getPensionDefaultGrowth(val.pensionType) : undefined);
    }
    updateStartAgeDisplay();
    lastAmountSynced = '';
    updateAmountInput();
  },
  { immediate: true },
);

watch(kind, (newKind) => {
  if (newKind !== EventKind.income) {
    showPension.value = false;
    pensionType.value = undefined;
    growthRate.value = undefined;
  }
});

watch(showPension, (checked) => {
  if (checked) {
    inflationAdjusted.value = false;
  } else {
    inflationAdjusted.value = true;
    pensionType.value = undefined;
    growthRate.value = undefined;
  }
});

watch(pensionType, (val, prev) => {
  if (val && showPension.value && prev !== val) {
    growthRate.value = getPensionDefaultGrowth(val);
  }
});

// --- Validation ---
const amountError = ref<string | null>(null);
const startAgeError = ref<string | null>(null);
const growthRateError = ref<string | null>(null);
const durationError = ref<string | null>(null);
const pensionTypeError = ref<string | null>(null);

function validateField(): { valid: boolean } {
  let valid = true;

  if (amount.value <= 0) {
    amountError.value = props.getText('eventValidationAmountPositive');
    valid = false;
  } else if (!Number.isInteger(amount.value)) {
    amountError.value = props.getText('eventValidationAmountInteger');
    valid = false;
  } else {
    amountError.value = null;
  }

  if (startAge.value < props.currentAge) {
    startAgeError.value = props.getText('eventValidationStartAge', { currentAge: props.currentAge });
    valid = false;
  } else {
    startAgeError.value = null;
  }

  if (showPension.value) {
    const effectiveGrowthRate = growthRate.value ?? (pensionType.value ? getPensionDefaultGrowth(pensionType.value) : 0);
    if (effectiveGrowthRate < 0 || effectiveGrowthRate > 0.2) {
      growthRateError.value = props.getText('eventValidationGrowthRate');
      valid = false;
    } else {
      growthRateError.value = null;
    }
  } else {
    growthRateError.value = null;
  }

  if (showPension.value && pensionType.value === 'bridge' && period.value === 'years' && durationYears.value > 5) {
    durationError.value = props.getText('eventValidationBridgeDuration');
    valid = false;
  } else {
    durationError.value = null;
  }

  if (showPension.value && pensionType.value && pensionType.value !== 'none') {
    const existingCount = (props.existingEvents ?? []).filter(
      (e) => e.pensionType === pensionType.value && e.id !== props.event?.id
    ).length;
    if (existingCount >= 2) {
      pensionTypeError.value = props.getText('eventValidationMaxPensionType');
      valid = false;
    } else {
      pensionTypeError.value = null;
    }
  } else {
    pensionTypeError.value = null;
  }

  return { valid };
}

// Real-time validation on form field changes
watch([amount, startAge, growthRate, pensionType, durationYears, period, showPension], () => {
  validateField();
});

function buildFormData(): EventFormData {
  return {
    kind: kind.value,
    label: label.value,
    amount: Math.round(Math.max(0, amount.value)),
    frequency: frequency.value,
    period: period.value,
    durationYears: period.value === 'years' ? durationYears.value : period.value === 'once' ? 1 : 0,
    startAge: Math.max(0, startAge.value),
    inflationAdjusted: inflationAdjusted.value && !showPension.value,
    pensionType: (showPension.value && pensionType.value) ? pensionType.value : undefined,
    growthRate: (showPension.value && pensionType.value) ? growthRate.value : undefined,
  };
}

function handlePrimary(): void {
  if (!validateField().valid) return;
  const data = buildFormData();
  if (isEdit.value) {
    emit('update', data);
  } else {
    emit('create', data);
  }
}

function handleSaveAsNew(): void {
  if (!validateField().valid) return;
  emit('saveAsNew', buildFormData());
}

function handleClose(): void {
  emit('close');
}

// --- Keyboard ---
function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    e.preventDefault();
    handleClose();
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('mousedown', onPensionDropdownClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
  document.removeEventListener('mousedown', onPensionDropdownClickOutside);
});

// --- Helpers ---
const derivedCalendarYear = computed(() => {
  const baseYear = new Date().getFullYear();
  return baseYear + (startAge.value - props.currentAge);
});

const startAgeYearHint = computed(() => {
  if (period.value === 'years' && durationYears.value > 0) {
    const endYear = derivedCalendarYear.value + durationYears.value;
    return `${props.getText('eventStartAgeYearHint')} ${derivedCalendarYear.value} – ${endYear}`;
  }
  return `${props.getText('eventStartAgeYearHint')} ${derivedCalendarYear.value}`;
});

const growthRateDisplay = computed({
  get: () => {
    if (growthRate.value != null) return growthRate.value * 100;
    if (pensionType.value) return (PENSION_VALIDATION[pensionType.value]?.growthRate ?? 0) * 100;
    return 0;
  },
  set: (v: number) => {
    const clamped = Math.max(0, Math.min(20, v));
    growthRate.value = Number((clamped / 100).toFixed(4));
  },
});

const pensionThresholdLabel = computed(() => {
  if (!pensionType.value || pensionType.value === 'none') return undefined;
  return getPensionThresholdLabel(pensionType.value, amount.value);
});

const pensionThresholdPillType = computed(() => {
  switch (pensionThresholdLabel.value) {
    case 'tooLow': return 'error';
    case 'tooHigh': return 'warning';
    default: return 'success';
  }
});

const pensionThresholdText = computed(() => {
  switch (pensionThresholdLabel.value) {
    case 'tooLow': return props.getText('eventThresholdTooLow');
    case 'average': return props.getText('eventThresholdAverage');
    case 'tooHigh': return props.getText('eventThresholdTooHigh');
    default: return '';
  }
});

const pensionTypeOptions = computed(() => {
  return Object.entries(PENSION_TYPE_LABELS)
    .filter(([key]) => key !== 'none')
    .map(([key, labelText]) => ({ value: key as PensionType, label: labelText }));
});

const amountHasError = computed(() => amountError.value != null);
const startAgeHasError = computed(() => startAgeError.value != null);

function onStartAgeInput(e: Event): void {
  const target = e.target as HTMLInputElement;
  const raw = target.value.replace(/[^0-9]/g, '');
  startAge.value = raw === '' ? props.currentAge : parseInt(raw, 10);
  updateStartAgeDisplay();
}

function incrementStartAge(): void {
  startAge.value = Math.min(120, startAge.value + 1);
  updateStartAgeDisplay();
}

function decrementStartAge(): void {
  startAge.value = Math.max(props.currentAge, startAge.value - 1);
  updateStartAgeDisplay();
}
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-grey-900/40 px-2xl"
      data-testid="event-dialog"
      role="dialog"
      aria-modal="true"
      @click.self="handleClose"
    >
      <div class="w-full max-w-lg bg-bg-card rounded-extra-large border border-border-light p-2xl">
        <!-- Heading row with close button -->
        <div class="flex items-start justify-between mb-lg">
          <h2 class="text-title-large font-semibold text-text-primary m-0">
            {{ isEdit ? getText('eventDialogTitleEdit') : getText('eventDialogTitleCreate') }}
          </h2>
          <button
            type="button"
            class="w-8 h-8 flex items-center justify-center rounded-full text-text-secondary hover:text-text-primary hover:bg-grey-200 transition-colors duration-fast"
            @click="handleClose"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <!-- Kind toggle -->
        <div
          class="relative w-full h-9 rounded-pill bg-grey-50 p-0.5 select-none mb-md"
          data-testid="event-kind-toggle"
        >
          <div
            class="absolute top-0.5 bottom-0.5 rounded-pill bg-bg-card shadow-mode-pill transition-all duration-spring ease-spring"
            :style="{
              width: 'calc(100% / 2)',
              left: kind === 'income' ? 0 : 'calc(50% - 2px)',
            }"
          />
          <div class="relative flex h-full">
            <button
              type="button"
              class="flex-1 h-full flex items-center justify-center gap-1.5 text-label-medium font-medium rounded-pill outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-1 focus-visible:ring-offset-grey-50 transition-colors duration-fast"
              :class="kind === 'income' ? 'text-text-primary font-bold' : 'text-text-secondary hover:bg-grey-200 active:bg-grey-300'"
              @click="kind = 'income'"
            >
              {{ getText('eventKindIncome') }}
            </button>
            <button
              type="button"
              class="flex-1 h-full flex items-center justify-center gap-1.5 text-label-medium font-medium rounded-pill outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-1 focus-visible:ring-offset-grey-50 transition-colors duration-fast"
              :class="kind === 'extraExpense' ? 'text-text-primary font-bold' : 'text-text-secondary hover:bg-grey-200 active:bg-grey-300'"
              @click="kind = 'extraExpense'"
            >
              {{ getText('eventKindExpense') }}
            </button>
          </div>
        </div>

        <!-- Label field -->
        <div class="mb-md">
          <RequiredLabel
            :text="getText('eventLabelLabel')"
            :required="true"
          />
          <input
            v-model="label"
            type="text"
            class="w-full h-10 px-2.5 rounded-small border border-border-input bg-grey-50 outline-none text-body-medium text-text-primary placeholder:text-text-tertiary"
            :placeholder="getText('eventLabelPlaceholder')"
          />
        </div>

        <!-- Two-column grid: desktop 2-col, mobile single -->
        <div class="grid grid-cols-2 gap-y-lg gap-x-xl">
          <!-- Row 1: Částka (col 1) + Frekvence (col 2) -->
          <!-- Amount field -->
          <div>
            <RequiredLabel
              :text="getText('eventAmountLabel')"
              :required="true"
              :has-error="amountHasError"
            />
            <div
              class="flex items-center h-10 rounded-small border"
              :class="amountHasError ? 'bg-error-container border-error' : 'bg-grey-50 border-border-input'"
            >
              <input
                :value="amountInputValue"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                class="flex-1 min-w-0 h-full px-2.5 bg-transparent outline-none text-body-medium text-text-primary placeholder:text-text-tertiary"
                data-testid="event-amount-input"
                @input="onAmountInput"
                @focus="onAmountFocus"
                @blur="onAmountBlur"
              />
              <span class="px-2.5 text-label-small text-text-tertiary">{{ getText('currencyCzk') }}</span>
            </div>
            <ErrorSlot :text="amountError" />
          </div>

          <!-- Frequency radio -->
          <div>
            <label class="block mb-1.5 text-label-medium font-medium text-text-secondary">
              {{ getText('eventFrequencyLabel') }}
            </label>
            <div class="flex flex-col gap-xs">
              <label
                v-for="opt in ['monthly', 'yearly'] as const"
                :key="opt"
                class="flex items-center gap-1.5 cursor-pointer"
              >
                <span class="relative flex items-center justify-center w-4 h-4">
                  <input
                    type="radio"
                    :value="opt"
                    v-model="frequency"
                    class="sr-only cursor-pointer"
                  />
                  <span class="absolute inset-0.5 rounded-full border border-border-input pointer-events-none transition-colors duration-fast" />
                  <span class="absolute inset-1 rounded-full bg-brand opacity-0 transition-opacity duration-fast" :class="[frequency === opt ? 'opacity-100' : '']" />
                </span>
                <span class="text-body-small text-text-primary">
                  {{ getText(opt === 'monthly' ? 'eventFrequencyMonthly' : 'eventFrequencyYearly') }}
                </span>
              </label>
            </div>
          </div>

          <!-- Row 2: Věk zahájení (col 1) + Období radios (col 2) -->
          <div>
            <RequiredLabel
              :text="getText('eventStartAgeLabel')"
              :required="true"
              :has-error="startAgeHasError"
            />
            <InputFieldGroup
              :has-error="startAgeHasError"
              stepper="plusminus"
              @increment="incrementStartAge"
              @decrement="decrementStartAge"
            >
              <input
                type="text"
                inputmode="numeric"
                autocomplete="off"
                class="flex-1 min-w-0 h-10 px-2.5 bg-transparent outline-none text-body-medium text-text-primary"
                data-testid="event-startage-input"
                :value="startAgeDisplayValue"
                @input="onStartAgeInput"
              />
            </InputFieldGroup>
            <div class="text-body-small text-text-secondary mt-xs">
              {{ startAgeYearHint }}
            </div>
            <ErrorSlot :text="startAgeError" />
          </div>

          <!-- Period radio -->
          <div>
            <label class="block mb-1.5 text-label-medium font-medium text-text-secondary">
              {{ getText('eventPeriodLabel') }}
            </label>
            <div class="flex flex-col gap-xs">
              <!-- Ongoing -->
              <label class="flex items-center gap-1.5 cursor-pointer">
                <span class="relative flex items-center justify-center w-4 h-4">
                  <input
                    type="radio"
                    value="ongoing"
                    v-model="period"
                    class="sr-only cursor-pointer"
                  />
                  <span class="absolute inset-0.5 rounded-full border border-border-input pointer-events-none transition-colors duration-fast" />
                  <span class="absolute inset-1 rounded-full bg-brand opacity-0 transition-opacity duration-fast" :class="[period === 'ongoing' ? 'opacity-100' : '']" />
                </span>
                <span class="text-body-small text-text-primary">
                  {{ getText('eventPeriodOngoing') }}
                </span>
              </label>
              <!-- Once -->
              <label class="flex items-center gap-1.5 cursor-pointer">
                <span class="relative flex items-center justify-center w-4 h-4">
                  <input
                    type="radio"
                    value="once"
                    v-model="period"
                    class="sr-only cursor-pointer"
                  />
                  <span class="absolute inset-0.5 rounded-full border border-border-input pointer-events-none transition-colors duration-fast" />
                  <span class="absolute inset-1 rounded-full bg-brand opacity-0 transition-opacity duration-fast" :class="[period === 'once' ? 'opacity-100' : '']" />
                </span>
                <span class="text-body-small text-text-primary">
                  {{ getText('eventPeriodOnce') }}
                </span>
              </label>
              <!-- Years + duration input below -->
              <div v-if="period === 'years'" class="flex flex-col gap-xs">
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <span class="relative flex items-center justify-center w-4 h-4">
                    <input
                      type="radio"
                      value="years"
                      v-model="period"
                      class="sr-only cursor-pointer"
                    />
                    <span class="absolute inset-0.5 rounded-full border border-border-input pointer-events-none transition-colors duration-fast" />
                    <span class="absolute inset-1 rounded-full bg-brand opacity-0 transition-opacity duration-fast" :class="[period === 'years' ? 'opacity-100' : '']" />
                  </span>
                  <span class="text-body-small text-text-primary">
                    {{ getText('eventPeriodYears') }}
                  </span>
                </label>
                <div class="ml-6">
                  <InputFieldGroup
                    :has-error="durationError != null"
                    stepper="plusminus"
                    @increment="() => { durationYears = Math.max(1, durationYears + 1); }"
                    @decrement="() => { durationYears = Math.max(1, durationYears - 1); }"
                  >
                    <input
                      v-model.number="durationYears"
                      type="text"
                      inputmode="numeric"
                      autocomplete="off"
                      class="flex-1 min-w-0 h-10 px-2.5 bg-transparent outline-none text-body-small text-text-primary"
                    />
                    <span class="px-2.5 text-body-small text-text-tertiary">{{ getText('eventDurationUnit') }}</span>
                  </InputFieldGroup>
                  <ErrorSlot :text="durationError" />
                </div>
              </div>
              <template v-if="period !== 'years'">
                <label class="flex items-center gap-1.5 cursor-pointer">
                  <span class="relative flex items-center justify-center w-4 h-4">
                    <input
                      type="radio"
                      value="years"
                      v-model="period"
                      class="sr-only cursor-pointer"
                    />
                    <span class="absolute inset-0.5 rounded-full border border-border-input pointer-events-none transition-colors duration-fast" />
                    <span class="absolute inset-1 rounded-full bg-brand opacity-0 transition-opacity duration-fast" :class="[period === 'years' ? 'opacity-100' : '']" />
                  </span>
                  <span class="text-body-small text-text-primary">
                    {{ getText('eventPeriodYears') }}
                  </span>
                </label>
              </template>
            </div>
          </div>
        </div>

        <!-- Pension section (income only, opt-in) -->
        <div v-if="pensionVisible" class="mt-md">
          <div class="flex items-center justify-between mb-sm">
            <span
              class="text-label-medium font-semibold text-brand"
            >
              {{ getText('eventPensionCheckbox') }}
            </span>
            <button
              type="button"
              role="switch"
              :aria-checked="showPension"
              data-testid="event-pension-checkbox"
              :class="[
                'relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-fast cursor-pointer border-none p-0',
                showPension ? 'bg-brand' : 'bg-grey-300',
              ]"
              @click="showPension = !showPension"
            >
              <span
                class="pointer-events-none inline-block h-5 w-5 rounded-full bg-bg-card shadow-elevation-level1 transform transition-transform duration-fast"
                :style="{ transform: showPension ? 'translateX(22px)' : 'translateX(2px)' }"
              />
            </button>
          </div>

          <div
            v-if="showPension"
            class="ml-lg mt-sm transition-all duration-fast"
          >
            <!-- Pension type + growth rate in one row -->
        <div class="grid grid-cols-2 gap-y-lg gap-x-xl">
              <div>
                <label class="block mb-1.5 text-label-medium font-medium text-text-secondary">
                  {{ getText('eventPensionTypeLabel') }}
                </label>
                <div class="relative">
                  <button
                    type="button"
                    data-testid="event-pension-type"
                    class="w-full h-10 px-2.5 pr-8 rounded-small border bg-grey-50 outline-none text-body-small text-text-primary flex items-center justify-between"
                    :class="pensionTypeError ? 'border-error bg-error-container' : 'border-border-input'"
                    @click="togglePensionDropdown"
                  >
                    <span v-if="pensionType" class="truncate">
                      {{ PENSION_TYPE_LABELS[pensionType] }}
                    </span>
                    <span v-else class="text-text-tertiary">
                      {{ getText('eventPensionTypePlaceholder') }}
                    </span>
                    <svg
                      class="absolute top-2.5 right-2.5 w-4 h-4 text-text-tertiary pointer-events-none transition-transform duration-fast"
                      :class="{ 'rotate-180': showPensionDropdown }"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <transition
                    enter-active-class="transition duration-fast ease-out"
                    enter-from-class="opacity-0 -translate-y-1"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition duration-fast ease-in"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 -translate-y-1"
                  >
                    <ul
                      v-if="showPensionDropdown"
                      class="absolute z-10 w-full mt-1 bg-bg-card border border-border-light rounded-small shadow-elevation-level2 overflow-hidden"
                    >
                      <li
                        v-for="opt in pensionTypeOptions"
                        :key="opt.value"
                        class="px-2.5 py-2 text-body-medium text-text-primary cursor-pointer hover:bg-grey-50 transition-colors duration-fast"
                        :class="{ 'bg-grey-50 font-medium': pensionType === opt.value }"
                        @mousedown.prevent="selectPensionType(opt.value)"
                      >
                        {{ opt.label }}
                      </li>
                    </ul>
                  </transition>
                </div>
                <ErrorSlot :text="pensionTypeError" />
                <div class="text-body-small text-text-secondary mt-xs">
                </div>
              </div>

              <div class="text-body-small">
                <PercentageInput
                  v-model="growthRateDisplay"
                  :label="getText('eventGrowthRateLabel')"
                  :min="0"
                  :max="20"
                  :error-text="growthRateError"
                />
              </div>
            </div>

            <!-- Threshold badge -->
            <div v-if="pensionThresholdLabel" class="mb-md">
              <span
                data-testid="event-threshold-badge"
                class="inline-block cursor-pointer"
              >
                <StatusPill
                  :type="pensionThresholdPillType"
                  :label="pensionThresholdText"
                />
              </span>
            </div>
          </div>
        </div>

        <!-- Inflation adjusted (always visible for income kind) -->
        <div v-if="pensionVisible" class="mt-md">
          <div class="flex items-center gap-sm">
            <input
              type="checkbox"
              v-model="inflationAdjusted"
              class="w-4 h-4 rounded border border-border-input accent-brand cursor-pointer"
              id="event-inflation-adjusted-income"
            />
            <label for="event-inflation-adjusted-income" class="text-label-medium font-medium text-text-secondary cursor-pointer">
              {{ getText('eventInflationAdjusted') }}
            </label>
          </div>
        </div>
        <!-- Inflation adjusted (when NOT income kind) -->
        <div v-if="!pensionVisible" class="mt-md">
          <div class="flex items-center gap-sm">
            <input
              type="checkbox"
              v-model="inflationAdjusted"
              class="w-4 h-4 rounded border border-border-input accent-brand cursor-pointer"
              id="event-inflation-adjusted-expense"
            />
            <label for="event-inflation-adjusted-expense" class="text-label-medium font-medium text-text-secondary cursor-pointer">
              {{ getText('eventInflationAdjusted') }}
            </label>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-between items-center mt-2xl pt-lg border-t border-border-light">
          <button
            type="button"
            class="px-md py-sm rounded-pill bg-bg-card border border-border-light text-text-secondary text-label-medium font-medium hover:bg-grey-200 active:bg-grey-300 focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-page transition-colors duration-fast"
            @click="handleClose"
          >
            {{ getText('eventActionCancel') }}
          </button>

          <button
            type="button"
            data-testid="dialog-primary-btn"
            class="px-md py-sm rounded-pill bg-brand text-grey-100 text-label-medium font-medium hover:opacity-90 focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-page transition-opacity duration-fast"
            @click="handlePrimary"
            @keyup.enter="handlePrimary"
          >
            {{ isEdit ? getText('eventActionSave') : getText('eventActionAdd') }}
          </button>

          <button
            v-if="isEdit"
            type="button"
            data-testid="dialog-save-as-new-btn"
            class="px-md py-sm rounded-pill bg-transparent border border-border-light text-brand text-label-medium font-medium hover:bg-grey-200 active:bg-grey-300 focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-page transition-colors duration-fast"
            @click="handleSaveAsNew"
          >
            {{ getText('eventActionSaveAsNew') }}
          </button>
          <span v-else />
        </div>
      </div>
    </div>
  </Teleport>
</template>
